import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { signOut } from '@/auth/actions'
import { runtimeConfig } from '@/config/runtime'
import { Icon } from '@/components/Icon'
import type { PlayerProfile } from '@/gen/player/v1/player_pb'
import { factionToken } from '@/map/geojson'
import { LanguageSelect } from './LanguageSelect'

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return (parts.length > 1 ? parts[0][0] + parts[1][0] : name.slice(0, 2)).toUpperCase() || '?'
}

export function AvatarMenu({ player }: { player: PlayerProfile | null }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const nickname = player?.nickname || t('shell.unnamed')

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2.5 py-1 pr-1 pl-1 text-left transition-colors hover:bg-panel-light"
      >
        <span
          className="soc-proper grid size-9 place-items-center bg-panel-raised text-[16px] text-ink"
          style={{ boxShadow: `inset 0 -3px 0 var(--soc-${factionToken(player?.faction)})` }}
        >
          {initials(nickname)}
        </span>
        <span className="soc-proper hidden max-w-[16ch] truncate text-[16px] text-ink md:block">{nickname}</span>
        <Icon name="chevronDown" size={14} className="text-ink-mute" />
      </button>
      {open && (
        <div role="menu" className="soc-card absolute top-full right-0 z-50 mt-2 w-64 bg-panel-raised py-2 shadow-[0_12px_32px_var(--soc-bg-deep)]">
          <button
            role="menuitem"
            type="button"
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-ink-soft hover:bg-panel-light hover:text-ink"
            onClick={() => {
              setOpen(false)
              if (player) navigate(`/map?player=${encodeURIComponent(player.playerId)}`)
            }}
          >
            <Icon name="user" size={16} />
            <span className="font-title text-[12px] tracking-[0.2em] uppercase">{t('shell.profile')}</span>
          </button>
          <div className="px-4 py-2.5">
            <div className="soc-label mb-2">{t('shell.language')}</div>
            <LanguageSelect className="w-full [&>select]:w-full" />
          </div>
          <div className="soc-divider my-1" />
          <button
            role="menuitem"
            type="button"
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-ink-soft hover:bg-panel-light hover:text-crimson-bright"
            onClick={() => {
              setOpen(false)
              void signOut()
            }}
          >
            <Icon name="logout" size={16} />
            <span className="font-title text-[12px] tracking-[0.2em] uppercase">{t('shell.signOut')}</span>
          </button>
          <p className="soc-num px-4 pt-2 text-[10px] text-ink-fade">
            {runtimeConfig().environment} · {__COMMIT_HASH__}
          </p>
        </div>
      )}
    </div>
  )
}
